import { Box } from "@mui/material";
import { ReactNode } from "react";

/**
 * Shared header-cell styling for Bots Management tables. Opaque background so a
 * `stickyHeader` row never shows body rows bleeding through while scrolling.
 */
export const BOTS_HEADER_CELL_SX = {
  fontWeight: 700,
  textAlign: "center" as const,
  verticalAlign: "middle" as const,
  borderBottom: 2,
  borderColor: "divider",
  whiteSpace: "nowrap" as const,
  bgcolor: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === "dark" ? "background.paper" : "grey.50",
};

/** Body-cell default: centered, vertically aligned, single-line (ellipsis via `noWrap` Typography). */
export const BOTS_BODY_CELL_SX = {
  textAlign: "center" as const,
  verticalAlign: "middle" as const,
};

interface BotsTableScrollProps {
  children: ReactNode;
  /**
   * Fill the parent flex area and scroll internally (sticky header stays put).
   * Use inside height-constrained panels. When false the wrapper sizes to its
   * content and only scrolls horizontally.
   */
  fill?: boolean;
  /** Draw the rounded outlined frame around the table (replaces `Paper variant="outlined"`). */
  bordered?: boolean;
  sx?: object;
}

/**
 * Unified scroller for every Bots Management table.
 *
 * The inner `<Table>` keeps a fixed `minWidth`, so when the panel narrows the
 * columns stay rigid and the area scrolls instead of squeezing / overlapping.
 * Uses the browser's native (globally styled, always-visible) scrollbars so a
 * horizontal bar appears whenever the table is wider than the viewport, and the
 * mouse wheel scrolls vertically everywhere.
 */
export default function BotsTableScroll({
  children,
  fill = false,
  bordered = true,
  sx,
}: BotsTableScrollProps) {
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        // Both axes scroll natively; horizontal bar shows on overflow, wheel drives vertical.
        overflow: "auto",
        // Reserve space for the vertical scrollbar at all times so the table doesn't
        // shrink / shift sideways the moment a scrollbar appears.
        scrollbarGutter: "stable",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        ...(fill ? { flex: 1, minHeight: 0 } : {}),
        ...(bordered
          ? {
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }
          : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
